import { useState } from 'react';
import { Table, Button, Modal, message, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import AffiliatorFormModal from './components/AffiliatorFormModal';
import { useGetAffiliators, useAddAffiliator, useUpdateAffiliator, useDeleteAffiliator } from 'api/affiliator';
import { Link } from 'react-router-dom';

const AffiliatorList = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [editingData, setEditingData] = useState<any>(null);

    const { data: affiliators, isFetching, error, refetch } = useGetAffiliators();
    const addMutation = useAddAffiliator();
    const updateMutation = useUpdateAffiliator();
    const deleteMutation = useDeleteAffiliator();

    const showEditModal = (record: any) => {
        setEditingData(record);
        setIsModalVisible(true);
    };

    const confirmDelete = (id: number) => {
        setDeletingId(id);
        setIsDeleteModalVisible(true);
    };

    const handleDelete = () => {
        if (deletingId) {
            deleteMutation.mutate(deletingId, {
                onSuccess: () => {
                    refetch();
                    setIsDeleteModalVisible(false);
                    setDeletingId(null);
                    message.success('Affiliator berhasil dihapus');
                },
                onError: (error) => {
                    console.error("Failed to delete:", error);
                    message.error('Gagal menghapus affiliator');
                },
            });
        }
    };

    const handleOk = (formData: any) => {
        if (editingData) {
            updateMutation.mutate(
                { id: editingData.id, data: formData }, {
                onSuccess: () => {
                    setEditingData(null);
                    setIsModalVisible(false);
                    refetch();
                    message.success('Affiliator berhasil diupdate');
                },
                onError: (error) => {
                    console.error("Failed to update:", error);
                    message.error('Gagal memperbarui affiliator');
                },
            });
        } else {
            addMutation.mutate(formData, {
                onSuccess: () => {
                    setIsModalVisible(false);
                    refetch();
                    message.success('Affiliator berhasil ditambahkan');
                },
                onError: (error) => {
                    console.error("Failed to add:", error);
                    message.error('Gagal menambahkan affiliator');
                },
            });
        }
    };

    const columns = [
        {
            title: 'No',
            key: 'index',
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: 'Nama Lengkap',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Kontak',
            key: 'contact',
            render: (_: any, record: any) => (
                <div>
                    <div>{record.phone}</div>
                    <div className="text-gray-500 text-xs">{record.email}</div>
                </div>
            )
        },
        {
            title: 'Kode Referral',
            dataIndex: 'affiliate_code',
            key: 'affiliate_code',
            render: (code: string) => <Tag color="blue">{code}</Tag>
        },
        {
            title: 'Tipe Fee',
            dataIndex: 'fee_type',
            key: 'fee_type',
            render: (type: string) => type === 'PERCENTAGE' ? 'Persentase (%)' : 'Nominal Tetap (Rp)'
        },
        {
            title: 'Besaran Fee',
            dataIndex: 'fee_amount',
            key: 'fee_amount',
            render: (amount: number, record: any) => record.fee_type === 'PERCENTAGE' ? `${amount}%` : `Rp ${amount.toLocaleString('id-ID')}`
        },
        {
            title: 'Aksi',
            key: 'action',
            render: (_: any, record: any) => (
                <div className="flex gap-2">
                    <Button
                        onClick={() => showEditModal(record)}
                        type="link"
                        icon={<EditOutlined />}
                    />
                    <Button
                        onClick={() => confirmDelete(record.id)}
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                    />
                </div>
            ),
        },
    ];

    if (error) return <p>Error loading affiliators</p>;

    return (
        <div className='bg-white p-4 rounded-lg'>
            <div className='flex justify-between items-center mb-6'>
                <h1 style={{ fontSize: 24, fontWeight: 'bold' }}>Daftar Affiliator</h1>
                <div className="flex gap-2">
                    <Link to="/affiliators/withdrawals">
                        <Button type="default">Riwayat Penarikan Dana</Button>
                    </Link>
                    <Button type="primary" onClick={() => setIsModalVisible(true)} icon={<PlusOutlined />}>Tambah Affiliator</Button>
                </div>
            </div>

            <Table
                loading={isFetching}
                columns={columns}
                dataSource={affiliators?.data?.data || []}
                rowKey="id"
                bordered
            />

            <AffiliatorFormModal
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={() => {
                    setEditingData(null);
                    setIsModalVisible(false);
                }}
                initialData={editingData}
            />

            <Modal
                title="Konfirmasi Hapus"
                visible={isDeleteModalVisible}
                onOk={handleDelete}
                onCancel={() => setIsDeleteModalVisible(false)}
                okText="Hapus"
                cancelText="Batal"
            >
                <p>Apakah Anda yakin ingin menghapus affiliator ini?</p>
            </Modal>
        </div>
    );
};

export default AffiliatorList;
